import { useCallback } from 'react';
import styled from 'styled-components';

import { DestructiveButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES } from 'ui/modal';
import { fontWeights } from 'ui/theme';
import { useToastNotification } from 'utils/hooks/useToastNotification';

import { useUserDisplayName, useUserKey } from '../../hooks';
import { ModalState, UnsubscribeFromMessageTypesModalState, useModalsContext } from '../contexts';
import {
  useFilteredModalState,
  useMessageTypeOrTypes,
  useModifyMessageTypeSubscriptionsMutation,
} from '../hooks';

const displayText = {
  title: 'Unsubscribe from message type?',
  bulkTitle: 'Unsubscribe from message types?',
  confirm: 'Unsubscribe',
  loading: 'Unsubscribing...',
};

const Bold = styled.b`
  font-weight: ${fontWeights.bold};
`;

const isUnsubscribingFromMessageTypes = (
  modalState?: ModalState | null,
): modalState is UnsubscribeFromMessageTypesModalState =>
  ((modalState as UnsubscribeFromMessageTypesModalState | undefined)?.unsubscribingFromMessageTypes
    ?.length ?? 0) > 0;

const getMessageTypes = (state: UnsubscribeFromMessageTypesModalState) =>
  state.unsubscribingFromMessageTypes;

export const UnsubscribeFromMessageTypesModal = () => {
  const userKey = useUserKey(true);
  const { hideModal } = useModalsContext();
  const unsubscribingTypes = useFilteredModalState(
    isUnsubscribingFromMessageTypes,
    getMessageTypes,
  );
  const userDisplayName = useUserDisplayName();

  const {
    mutate: modifySubscription,
    reset,
    isSuccess,
    isLoading,
    isError,
  } = useModifyMessageTypeSubscriptionsMutation();

  const messageTypeOrTypes = useMessageTypeOrTypes(unsubscribingTypes);

  const onDone = useCallback(() => {
    hideModal();
    reset();
  }, [hideModal, reset]);

  useToastNotification({
    enabled: messageTypeOrTypes.length > 0 && (isSuccess || isError),
    onDone,
    positive: isSuccess,
    negative: isError,
    positiveMessage: (
      <>
        Unsubscribed <Bold>{userDisplayName}</Bold> from <Bold>{messageTypeOrTypes}</Bold>
      </>
    ),
    negativeMessage: (
      <>
        Could not unsubscribe <Bold>{userDisplayName}</Bold> from <Bold>{messageTypeOrTypes}</Bold>
      </>
    ),
  });

  if (unsubscribingTypes.length === 0) {
    return null;
  }

  const handleSubmit = () => {
    modifySubscription({
      userKey,
      messageTypeIds: unsubscribingTypes.map(({ id }) => id),
      subscribe: false,
    });
  };

  return (
    <Modal
      id="UnsubscribeFromMessageTypesModal"
      isModalOpen
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>
        {unsubscribingTypes.length === 1 ? displayText.title : displayText.bulkTitle}
      </ModalHeader>
      <ModalBody>
        Are you sure that you want to unsubscribe <Bold>{userDisplayName}</Bold> from{' '}
        <Bold>{messageTypeOrTypes}</Bold>?
      </ModalBody>
      <ModalFooter>
        <DestructiveButton
          onClick={handleSubmit}
          data-test="confirm-unsubscribe-button"
          isDisabled={isLoading}
          size={BUTTON_SIZES.LARGE}
        >
          {isLoading ? displayText.loading : displayText.confirm}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
